import { canAccessFeature } from '../../utils/permissions'
import { useWorkspaceRole } from '../../utils/useWorkspaceRole'
import { Card, EmptyState } from '../../ds'

/**
 * The owner/admin presentation gate for workspace settings.
 *
 * ⚠️ **This is a relocation, so the access boundary has to move too.** Store and Integrations
 * were tabs on `screens/Account.tsx`, whose single `canAccessFeature(role, 'account')` check at
 * the top of the screen covered every control inside them. `screens/Authority.tsx` has no such
 * check — `canAccessFeature` appeared in exactly three files in this app at the time (`Account.tsx`,
 * the since-deleted `BillingView.tsx`, `TeamView.tsx`) — so moving the tabs without moving the gate would have
 * handed every `member` the workspace name, the store facts Marva states on the merchant's
 * authority, the archetype override, Shopify OAuth connect/reconnect, webhook
 * connect/disconnect, the email sending domain and the Twilio credentials.
 *
 * This wrapper is a UX boundary, never the authorization control. Since MAR-170,
 * `PUT /workspaces/{id}` independently requires a Clerk `owner`/`admin` (and refuses a member or
 * API key). Keep the wrapper so members are not offered controls the server will reject.
 *
 * MAR-170 also uses this around the remaining controls that write through that endpoint. Keep
 * read-only receipts and controls owned by other authorization contracts visible; wrap the
 * workspace-setting writer, not the whole Authority screen.
 *
 * ⚠️ **The role comes from `useWorkspaceRole`, the same hook the controls beside it use**
 * (the mention-grant card on Limits, MAR-296). It used to read `currentUser.role` while the
 * card read the membership, so one tab could call the same person an owner and a member at
 * once. Account and Billing gate on the same hook. A membership that has not loaded is "no
 * role" here — the restricted card — exactly as on TeamView; there is no `currentUser`
 * fallback (see `useWorkspaceRole` for why PR #237 round 4 removed it).
 */
export default function RestrictedTab({
  children,
  hideWhenRestricted = false,
}: {
  children: React.ReactNode
  /** Use for a second writer on a mixed tab after the tab has already explained the restriction. */
  hideWhenRestricted?: boolean
}) {
  const { role } = useWorkspaceRole()

  if (!canAccessFeature(role, 'account')) {
    if (hideWhenRestricted) return null

    return (
      <Card>
        <EmptyState title="Access restricted">
          You don&apos;t have permission to change workspace settings. Ask a workspace owner or admin
          for access.
        </EmptyState>
      </Card>
    )
  }

  return <>{children}</>
}
